import { lazy, Suspense } from 'react';
import { createPortal } from 'react-dom';
import { useIsMobile } from './hooks/useMediaQuery';

//Lazy (plan 5.1): react-joyride stays out of the entry chunk; desktop
//fetches it in parallel on mount, mobile (tours disabled) never does.
const Joyride = lazy(() => import('react-joyride'));

/**
 * Popper (through react-floater) measures a step against the target's nearest
 * scrolling ancestor, not against the screen. The Settings rail is
 * `overflow: auto` and 256px wide, so a 384px tooltip placed `right` of a rail
 * row does not "fit" — and floater's fallback chain for `right` is
 * ['right','bottom-end','top-end','left','top-start','bottom-start'], so it
 * settles on `bottom-end`, which right-aligns the tooltip to a 239px row and
 * puts its left edge — and the Next button — at -138px.
 *
 * Joyride already rewrites `preventOverflow` to the window when it detects a
 * custom scroll parent, but it never touches `flip`, which is the modifier that
 * chose the placement. So pin both to the viewport: that is the only boundary
 * the user can actually see.
 *
 * PRECEDENCE: defaults < these props < step.floaterProps. Steps stay free to
 * override. `getMergedStep` builds what react-floater actually receives with
 * `deepmerge.all([defaultFloaterProps, props.floaterProps, step.floaterProps])`
 * — step last, step wins — and `JoyrideStep.render` spreads that result.
 *
 * Do not be misled by the OTHER floaterProps merge in the same file.
 * `getStyles` computes `deepmerge(step.floaterProps, floaterProps)`, which does
 * put the component's props last, but its result is used only to derive
 * `floaterStyles`; it never reaches Popper. Reading that one and not this one is
 * how this comment briefly came to claim the opposite.
 *
 * ONE THING HERE REALLY IS OVERRULED, and not by precedence: after the merge,
 * `getMergedStep` force-sets `preventOverflow.boundariesElement = "window"`
 * whenever the target has a custom scroll parent. Every target on a screen whose
 * content pane is `overflow: auto` — Settings, Payment Insights — takes that
 * path, so the `'viewport'` below applies to `flip` but NOT to the final
 * preventOverflow clamp on those screens. In Popper v1 "window" means the whole
 * document, so a tooltip can still be positioned outside the visible area; that
 * is what `hooks/tourScroll.js` exists to prevent by keeping the target on
 * screen in the first place.
 *
 * Exported because three tours render Joyride directly rather than through this
 * portal: CommonThead, AddEditInventory, AddEditCommitted. DashGrid was a
 * fourth until the per-card tours were removed; it renders no Joyride now.
 */
/**
 * WHY `flip.behavior` IS SET HERE.
 *
 * react-floater hands Popper `behavior: 'flip'` for any step placed `top` or
 * `bottom`, and 'flip' means exactly two candidates — the placement and its
 * opposite. So a `bottom` step that fits neither below nor above its target has
 * nowhere left to go, and `preventOverflow` shoves it back inside the viewport,
 * which for a target taller than the space available means ON TOP of the thing
 * the step is describing. Measured on the dashboard at 1280x720
 * (scripts/probe-tour-placement.mjs): "Collection against target" covered 95%
 * of its own card and "Outstanding and overdue credit" 94%, with room free to
 * the right of both. Nine of the ten dashboard steps declare no placement at
 * all, so they inherit `bottom` and all of them are exposed to this.
 *
 * Popper walks this array looking for the CURRENT placement and moves to the
 * next entry, so one list gives every placement the tail after it:
 *
 *     bottom → right → left → top     right → left → top
 *     left   → top                    top   → (terminal)
 *
 * THE SIDES COME BEFORE `top`, for two reasons. The first is preference: beside
 * the card is what was asked for, and a tooltip above a card the reader has
 * just scrolled to is further from it than one alongside. The second is
 * mechanical, and it is why `top` has to be LAST rather than second. The flip
 * loop only re-tests a placement when a later entry follows it, so a terminal
 * entry is one Popper will settle on and let `preventOverflow` clamp. With
 * `top` second, a `bottom` step that flipped to `top` got re-tested, failed by
 * a few pixels and carried on to the sides — measured at 1440x900, that moved
 * "What to act on today" off a clean `above` and onto its own card. As the last
 * entry `top` keeps the old terminal behaviour, so nothing that used to land
 * above still fits and then moves.
 *
 * NOT `behavior: 'clockwise'`, which looks like the right answer and is a no-op:
 * popper.js v1 builds that order with `clockwise()`, which EXCLUDES the starting
 * placement, and the flip loop only acts on the entry matching the current
 * placement — so it never matches and never flips.
 */
const FLIP_ORDER = ['bottom', 'right', 'left', 'top'];

export const TOUR_FLOATER_PROPS = {
    options: {
        flip: { boundariesElement: 'viewport', behavior: FLIP_ORDER },
        preventOverflow: { boundariesElement: 'viewport' },
    },
};

export default function JoyridePortal({ children, floaterProps, ...joyrideProps }) {
    //Tours are desktop-only: their steps target desktop-strip elements
    //(#tab-*, floating buttons) that don't exist in the mobile shell.
    const isMobile = useIsMobile();
    if (isMobile) return null;

    const mergedFloaterProps = {
        ...TOUR_FLOATER_PROPS,
        ...floaterProps,
        options: { ...TOUR_FLOATER_PROPS.options, ...floaterProps?.options },
    };

    // Render Joyride in a portal at the end of document.body
    return createPortal(
        <Suspense fallback={null}>
            <Joyride {...joyrideProps} floaterProps={mergedFloaterProps} />
        </Suspense>,
        document.body
    );
}
